import {Button, Dialog, InputGroup, Text} from "@blueprintjs/core";
import log from "loglevel";
import styled from "@emotion/styled";
import {debounce} from "lodash";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {AppContext} from "../../ApplicationContext";
import {
  Job,
  Manifest,
  MultiMatchOperator,
  MultiMatchType,
  SearchableSortDirection,
  SkillOverrideNoteInput,
  useAssignJobsToManifestMutation,
  useSearchManifestDetailsLazyQuery
} from "../../generated/graphql";
import {getArrayFromField} from "../../utils/General";
import {activePendingActions} from "../../utils/assignment/PendingAssignment";
import {
  buildSkillOverrideNotes,
  getRequirementGap,
  isAssignmentSubmittedNotificationEnabled
} from "../../utils/assignment/Assignment.utils";
import {DEFAULT_JOB_FIELDS} from "../../utils/dynamic-query/job-query-builder";
import {useAuthState} from "../../providers/AuthProvider";
import {Constants, TenantPreferences} from "../../constants/constants";
import {useGetJob} from "../../hooks/useGetJob";
import InitContent from "./components/InitContent";
import ManifestItem, {TOnAssigedJobFunction} from "./components/ManifestItem";
import {LoadingSpinner} from "../common/LoadingSpinner";
import {useOktaAuth} from "@okta/okta-react";
import {GlobalFindSkillMessage, JobErrorMessage} from "./enums";
import {useAssignmentDispatch, useAssignmentState} from "../../providers/AssignmentProdiver";
import {TManifestEnhanced} from "../common/types";
import {useJobsEverCompleted} from "../../hooks/useJobsEverCompleted";
import {extractSimplePref, usePreferenceContext} from "../../providers/PreferenceProvider";
import {useFeatureFlagContext} from "../../providers/FeatureFlagProvider";
import {useDispatchDataContext} from "../../providers/DispatchDataProvider";
import {DriverRequirementEnforcementLevel} from "../settings/enums/DriverRequirementEnforcementLevel";
import {AssignJobsConfirmation} from "../manifest/details/components/AssignJobsConfirmation";

// The default lookup has no skills, so without these the skill check would never fire.
export const GLOBAL_FIND_JOB_FIELDS = [...DEFAULT_JOB_FIELDS, "order.orderSkills.id", "order.orderSkills.name"];

type TAssignResult = Awaited<ReturnType<TOnAssigedJobFunction>>;
type TSkillCheck = {job: Job; manifest: TManifestEnhanced};

const ASSIGNED: TAssignResult = {success: true, message: "Success"};
// An empty message keeps ManifestItem from showing an error: the dispatcher chose not to assign.
const NOT_ASSIGNED: TAssignResult = {success: false, message: ""};

const GlobalFind = () => {
  const {dispatch} = useContext(AppContext);
  const assignmentState = useAssignmentState();
  const assignmentStateDispatch = useAssignmentDispatch();

  const authState = useAuthState();
  const {oktaAuth} = useOktaAuth();
  const {tenantPreferences} = usePreferenceContext();
  const {skills: skillsEnabled} = useFeatureFlagContext();
  const {skills} = useDispatchDataContext();
  const [initiallizing, setInitiallizing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [manifests, setManifests] = useState<TManifestEnhanced[]>([]);
  const [totalManifests, setTotalManifests] = useState(0);
  const [selectedManifestId, setSelectedManifestId] = useState<number | undefined>(undefined);
  // The last check stays set after the modal closes: Blueprint returns focus to the job input only when the
  // dialog plays its exit transition, which needs the dialog mounted with its content.
  const [skillCheck, setSkillCheck] = useState<TSkillCheck | null>(null);
  const [isSkillCheckOpen, setIsSkillCheckOpen] = useState(false);
  const skillCheckResolverRef = useRef<((result: TAssignResult) => void) | null>(null);

  const [searchManifest, {refetch}] = useSearchManifestDetailsLazyQuery({});
  const [assignJobsToManifest] = useAssignJobsToManifestMutation();

  const {getJob} = useGetJob(GLOBAL_FIND_JOB_FIELDS);
  const {getEverCompletedJobs} = useJobsEverCompleted();
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isSkillsBased = useMemo(
    () =>
      (extractSimplePref(tenantPreferences, TenantPreferences.skillsBasedRequirements, false).value as boolean) &&
      skillsEnabled,
    [skillsEnabled, tenantPreferences]
  );

  const driverRequiredSetting = useMemo(
    () =>
      extractSimplePref(
        tenantPreferences,
        TenantPreferences.driverRequirementEnforcement,
        DriverRequirementEnforcementLevel.Allow
      ).value as DriverRequirementEnforcementLevel,
    [tenantPreferences]
  );

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  const onClose = useCallback(() => {
    dispatch({type: "SetIsGlobalFindOpen", payload: false});
  }, [dispatch]);

  const assignJob = useCallback(
    (job: Job, manifest: TManifestEnhanced, skillOverrideNotes?: SkillOverrideNoteInput[]) => {
      assignmentStateDispatch({
        type: "AssignJobsToManifest",
        payload: {
          isFromGlobalFind: true,
          assignFunction: assignJobsToManifest,
          authState: authState,
          selectedJobs: [job],
          selectedManifest: manifest,
          authToken: oktaAuth.getAccessToken() ?? "",
          showSubmittedToast: isAssignmentSubmittedNotificationEnabled(tenantPreferences),
          ...(skillOverrideNotes?.length ? {skillOverrideNotes} : {})
        }
      });
    },
    [assignJobsToManifest, assignmentStateDispatch, authState, oktaAuth, tenantPreferences]
  );

  const settleSkillCheck = useCallback((result: TAssignResult) => {
    const resolve = skillCheckResolverRef.current;
    skillCheckResolverRef.current = null;
    setIsSkillCheckOpen(false);
    resolve?.(result);
  }, []);

  const confirmSkillCheck = useCallback(
    ({job, manifest}: TSkillCheck, reasons: Record<number, string>) => {
      // Same job, driver and active skills as the modal, so the note names exactly the missing line's skills.
      const notes = buildSkillOverrideNotes({
        jobs: [job],
        reasons,
        driverSkills: manifest.driver?.skills,
        activeSkills: skills
      });
      assignJob(job, manifest, notes);
      settleSkillCheck(ASSIGNED);
    },
    [assignJob, settleSkillCheck, skills]
  );

  const onAssigedJob: TOnAssigedJobFunction = useCallback(
    async (manifest, jobNumber) => {
      const {data, errors} = await getJob(jobNumber);

      if (errors) {
        return {success: false, message: JobErrorMessage.ErrorOccurred};
      }
      if (data?.searchJobs?.items.length > 0) {
        const foundedJob = data.searchJobs.items[0] as Job;
        const pendingAction = activePendingActions(assignmentState.pendingActions).find((x) => {
          return x.jobIds.includes(foundedJob.jobId);
        });
        if (pendingAction) {
          return {success: false, message: JobErrorMessage.AlreadySummitted};
        }
        if (Constants.UNASSIGNED_JOB_STATUSES.includes(foundedJob.jobStatus)) {
          try {
            const jobInfoMap = new Map([
              [foundedJob.jobId, {jobNumber: foundedJob.jobNumber, orderId: foundedJob.order?.orderId}]
            ]);
            const everCompletedResult = await getEverCompletedJobs([foundedJob.jobId], jobInfoMap);
            if (everCompletedResult && everCompletedResult.length > 0) {
              return {success: false, message: JobErrorMessage.EverCompleted};
            }
          } catch {
            return {success: false, message: JobErrorMessage.ErrorOccurred};
          }

          const isMissingSkills =
            isSkillsBased &&
            driverRequiredSetting !== DriverRequirementEnforcementLevel.Allow &&
            getRequirementGap({
              order: foundedJob.order,
              driver: manifest.driver,
              isSkillsBasedRequirements: true,
              activeSkills: skills
            }).missing.length > 0;

          if (isMissingSkills) {
            // A second submit while the modal is open must not leave the first one waiting forever.
            skillCheckResolverRef.current?.(NOT_ASSIGNED);
            return new Promise<TAssignResult>((resolve) => {
              skillCheckResolverRef.current = resolve;
              setSkillCheck({job: foundedJob, manifest});
              setIsSkillCheckOpen(true);
            });
          }

          assignJob(foundedJob, manifest);
          return ASSIGNED;
        } else {
          return {success: false, message: `${JobErrorMessage.AlreadyAssigned} ${foundedJob.driver?.driverCode}`};
        }
      }
      return {success: false, message: JobErrorMessage.JobNumberNotExist};
    },
    [
      assignJob,
      assignmentState.pendingActions,
      driverRequiredSetting,
      getEverCompletedJobs,
      getJob,
      isSkillsBased,
      skills
    ]
  );

  const onSelectItem = useCallback((manifestDriverId: number) => {
    setSelectedManifestId(manifestDriverId);
  }, []);

  const onSearchManifest = useCallback(
    (keyword: string, offset?: number) => {
      searchManifest({
        variables: {
          filter: {
            multiMatch: {
              fields: ["driver.code", "stops.job.jobNumber", "stops.job.routeNumber"],
              query: keyword,
              operator: MultiMatchOperator.Or,
              type: MultiMatchType.Phrase
            },
            manifest: {
              or: [{manifestStatus: {match: "A"}}, {manifestStatus: {match: "I"}}, {manifestStatus: {match: "F"}}]
            }
          },
          limit: 10,
          offset: offset,
          sort: [
            {
              field: "driver.code.keyword",
              direction: SearchableSortDirection.Asc
            },
            {
              field: "manifestDate",
              direction: SearchableSortDirection.Asc
            },
            {
              field: "manifestDriverId",
              direction: SearchableSortDirection.Asc
            }
          ]
        },
        onCompleted(data) {
          setManifests((prev) => [...prev, ...getArrayFromField(data.searchManifests?.items)] as TManifestEnhanced[]);
          setLoading(false);
          setTotalManifests(data.searchManifests?.total ?? 0);
        },
        onError() {
          if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = setTimeout(() => {
            log.debug("refetching");
            refetch();
          }, 1500);
        }
      });
    },
    [refetch, searchManifest]
  );

  const onLoadMore = useCallback(() => {
    onSearchManifest(query, manifests.length);
  }, [manifests.length, onSearchManifest, query]);

  const handleQueryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const queryString = event.target.value;
      setQuery(queryString);
      setManifests([]);
      setSelectedManifestId(undefined);
      if (queryString.length > 0) {
        setInitiallizing(false);
        setLoading(true);
        onSearchManifest(queryString);
      }
      if (queryString.length === 0) {
        setInitiallizing(true);
      }
    },
    [onSearchManifest]
  );

  const renderBody = useCallback(() => {
    if (loading) {
      return (
        <CenterDiv>
          <LoadingSpinner size={25} />
        </CenterDiv>
      );
    }

    if (initiallizing) {
      return <InitContent />;
    }

    if (manifests.length === 0) {
      return (
        <CenterDiv>
          <NotFoundText data-testid="not-found-text">
            We couldn’t find any manifests with a matching job number, driver number, or route number.
          </NotFoundText>
        </CenterDiv>
      );
    }

    return (
      <InfiniteScroll
        next={onLoadMore}
        dataLength={manifests.length}
        hasMore={manifests.length < totalManifests}
        scrollableTarget={"option-infinity-scroll-list"}
        loader={""}
      >
        <List>
          {manifests.map((item) => {
            return (
              <ManifestItem
                key={item.manifestDriverId}
                item={item}
                isSelected={item.manifestDriverId === selectedManifestId}
                onSelect={onSelectItem}
                onAssigedJob={onAssigedJob}
              />
            );
          })}
        </List>
      </InfiniteScroll>
    );
  }, [initiallizing, loading, manifests, onAssigedJob, onLoadMore, onSelectItem, selectedManifestId, totalManifests]);

  return (
    <StyledDialog isOpen={true} onClose={onClose} canOutsideClickClose={false} portalClassName="global-find-portal">
      <Header data-testid="global-find-header">
        <StyledInput
          placeholder="Search..."
          onChange={debounce(handleQueryChange, 500)}
          data-testid="search-manifest-input"
          autoFocus
        />
        <Button className="close-button" data-testid="close-button" icon="cross" minimal onClick={onClose} />
      </Header>
      <Body id={"option-infinity-scroll-list"} data-testid="global-find-body">
        {renderBody()}
      </Body>
      {/* Shares Global Find's portal class (z-index 21) so it stacks above it; the default portal sits below. */}
      {skillCheck && (
        <AssignJobsConfirmation
          // Another job or driver gets a fresh modal, so a reason typed for one never carries over to the other.
          key={`${skillCheck.manifest.manifestDriverId}-${skillCheck.job.jobId}`}
          testIdPrefix="global-find-assign-job"
          isVisible={isSkillCheckOpen}
          assignmentState={{
            ...assignmentState,
            selectedJobs: [skillCheck.job],
            selectedStops: [],
            selectedManifest: skillCheck.manifest
          }}
          driverRequiredSetting={driverRequiredSetting}
          isSkillsBasedRequirements={true}
          bodyText={
            driverRequiredSetting === DriverRequirementEnforcementLevel.Warning
              ? GlobalFindSkillMessage.Warning
              : GlobalFindSkillMessage.Prevent
          }
          warningButtonLabels={{cancel: "Cancel", confirm: "Yes"}}
          portalClassName="global-find-portal"
          handleConfirm={(reasons) => confirmSkillCheck(skillCheck, reasons)}
          handleClose={() => settleSkillCheck(NOT_ASSIGNED)}
        />
      )}
    </StyledDialog>
  );
};

export default GlobalFind;

const StyledDialog = styled(Dialog)`
  align-self: flex-start;
  margin-top: 25vh;
  width: 35vw;
  min-width: 600px;
  max-width: 770px;
  max-height: 355px;
  border-radius: 16px;
  background: #ffffff;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  overflow: hidden;
`;

const Header = styled.div`
  position: relative;
  border-bottom: solid 1px #e5e7eb;

  .close-button {
    position: absolute;
    right: 12px;
    top: 12px;
  }
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
`;

const StyledInput = styled(InputGroup)`
  flex: 1;

  .bp5-input {
    height: 52px;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 0 48px 0 16px;
    border: none;
    box-shadow: none;
  }
`;

const List = styled.ul`
  list-style: none;
  text-overflow: ellipsis;
  padding: 0;
  margin: 0;
`;

const NotFoundText = styled(Text)`
  text-align: center;
  font-style: italic;
  color: #797979;
  padding: 30px 16px;
`;

const CenterDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 220px;
`;
